import { IonPage } from '@ionic/react'
import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import { useGetUnitSiteUsers } from '@apps-common/apis/portal/users'
import { MyMonitoringUsers, SystemNamedUsers } from '@apps-common/apps/portal/pages/units/pages/manage/pages/users/components'
import { PullRefresh } from '@apps-common/components/common'
import { MyMonitoringLogoOnly } from '@apps-common/components/common/logos'
import { AccountNoTitle, PageTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundLg, ButtonTab } from '@apps-common/components/elements/button/'
import { Header, LayoutContainer, LayoutPadding, VertSpacerLg, VertSpacerXl } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { ROUTE_UNIT_USERS } from '@apps-radionet-portal/config/private-routes'

import { MyMonitoringUserModifyModal } from './modals/my-monitoring-modify-modal'
import { SystemUserModifyModal } from './modals/system-user-modify-modal'

export type UnitUserType = 'myMonitoring' | 'system'

export const UnitUsersPage: React.FC = () => {
  const { status, unitId } = useParams<{ status: string; unitId: string }>()
  const { data: unit, isLoading: unitIsLoading, isError: unitIsError } = useGetUnitGenericDetails({ status, unitId })

  const { accountNo, ipAddress } = unit || {}
  const {
    data: users,
    isLoading: usersIsLoading,
    isFetching: usersIsFetching,
    isError: usersIsError,
    refetch: usersRefetch
  } = useGetUnitSiteUsers({ config: {}, unitId })

  const [tab, setTab] = useState<UnitUserType>('myMonitoring')
  const SystemUsersIcon = commonIcons.users

  const loading = unitIsLoading || unitIsError || unitIsError || usersIsLoading || usersIsError

  return (
    <IonPage>
      <Header classes='!max-w-[1200px]'>
        <Navbar showBack transparent />
        <Breadcrumbs route={ROUTE_UNIT_USERS} />
        <div className='flex items-center justify-between'>
          <PageTitle classes='flex items-center justify-start gap-[10px]'>
            <span className='block'>Users</span>
            <AccountNoTitle text={`(Acc# ${accountNo})`} />{' '}
          </PageTitle>
          {tab === 'myMonitoring' && (
            <ModalButton
              component={MyMonitoringUserModifyModal}
              disabled={loading || status === UNIT_STATUS_CANCELLED.value}
              componentProps={{
                unit: {
                  unitId,
                  ipAddress
                },
                existingIds: users?.myMonitoringUsers?.map((u) => u.id) || []
              }}
              // classes='relative bg-primary-content/5 w-[40px] h-[40px] rounded-full flex items-center justify-center'
              enterAnimation={modalSlideUpEnterAnimation}
              leaveAnimation={modalSlideUpLeaveAnimation}
            >
              <ButtonRoundLg
                type='div'
                loading={loading}
                disabled={loading || status === UNIT_STATUS_CANCELLED.value}
                className='action-success'
                tooltip={{ id: 'device-add-new-my-monitoring-user', place: 'bottom', message: 'Add new myMonitoring user' }}
              >
                <commonIcons.add className='!text-[1.1rem]' />
              </ButtonRoundLg>
            </ModalButton>
          )}
          {tab === 'system' && (
            <ModalButton
              component={SystemUserModifyModal}
              disabled={loading || status === UNIT_STATUS_CANCELLED.value}
              componentProps={{
                unit: {
                  unitId,
                  ipAddress
                }
              }}
              // classes='relative bg-primary-content/5 w-[40px] h-[40px] rounded-full flex items-center justify-center'
              enterAnimation={modalSlideUpEnterAnimation}
              leaveAnimation={modalSlideUpLeaveAnimation}
            >
              <ButtonRoundLg
                type='div'
                loading={loading}
                disabled={loading || status === UNIT_STATUS_CANCELLED.value}
                className='action-success'
                tooltip={{ id: 'device-add-new-system-user', place: 'bottom', message: 'Add new system user' }}
              >
                <commonIcons.add className='!text-[1.1rem]' />
              </ButtonRoundLg>
            </ModalButton>
          )}
        </div>
        <VertSpacerLg />
        <div className='flex gap-[10px] sm:gap-[20px]'>
          <ButtonTab classes='w-full sm:w-auto sm:min-w-[180px]' active={tab === 'myMonitoring'} callback={() => setTab('myMonitoring')}>
            <MyMonitoringLogoOnly color='fill-text' classes='w-[22px]' />
            myMonitoring
          </ButtonTab>
          <ButtonTab classes='w-full sm:w-auto sm:min-w-[180px]' active={tab === 'system'} callback={() => setTab('system')}>
            <SystemUsersIcon />
            System
          </ButtonTab>
        </div>
      </Header>
      <LayoutContainer>
        <LayoutPadding classes='pt-[70px] md:pt-[80px]'>
          <PullRefresh loading={usersIsFetching} callback={() => usersRefetch()} />
          <div className='relative overflow-x-hidden'>
            <AnimatePresence mode='wait'>
              {tab === 'myMonitoring' && (
                <MyMonitoringUsers
                  key='unit-my-monitoring-users'
                  status={status}
                  unitId={unitId}
                  ipAddress={ipAddress || ''}
                  users={users?.myMonitoringUsers || []}
                  loading={loading}
                />
              )}
              {tab === 'system' && (
                <SystemNamedUsers
                  key='unit-system-named-users'
                  status={status}
                  unitId={unitId}
                  ipAddress={ipAddress || ''}
                  users={users?.namedUsers || []}
                  loading={loading}
                />
              )}
            </AnimatePresence>
          </div>
        </LayoutPadding>
        <VertSpacerXl />
      </LayoutContainer>
    </IonPage>
  )
}
